// src/contexts/InterviewContext.tsx
import React, {
  createContext,
  useState,
  useRef,
  ReactNode,
  useEffect,
} from "react";
import { gcpTextToSpeech } from "../api/gcp";
import { useTranscription } from "../hooks/useTranscription";
import { httpsCallable } from "firebase/functions";
import { functions } from "../api/firebase";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { useMediaPermission } from "./MediaPermissionContext";
import { getRandomItem } from "../utils/common";

import { InterviewSession } from "../utils/types";
import { throttle } from "../utils/throttle";
import { useAuth } from "./AuthContext";
import { getAudioDuration } from "../utils/audioUtils";

// Welcome messages array
const WELCOME_MESSAGES = [
  "Welcome! I'm your interviewer for today. Before we begin, I want to mention two important things: First, to make this feel like a real interview, you won't be able to retry questions - this helps build authentic interview habits. Second, when we finish, please keep the window open for a few moments while I process our conversation. Are you ready to start?",
  "Hi there! I'll be conducting your interview today. Just a couple of quick notes: To create a realistic interview environment, each question will be a one-time opportunity - just like in a real interview. Also, when we finish, please don't close your browser window right away; I'll need a moment to compile my notes. Let me know when you're ready to begin!",
  "Hello! Thank you for joining this interview session. Two important points before we start: First, to maintain authenticity, there won't be any do-overs for questions - this helps prepare you for real interviews. And when we finish, please keep your browser window open for a few seconds while I gather my final thoughts. Shall we begin?",
  "Greetings! I'm your interviewer today. Quick housekeeping: To mirror real interview conditions, you'll get one attempt per question - this helps develop strong interview skills. Also important: when we're done, please wait a few moments before closing the window so I can properly process our session. Let me know when you're ready to begin!",
  "Hi! Welcome to your interview session. Before we dive in, let me share two key points: First, each question will be a single opportunity - just like in actual interviews. This helps build authentic interview skills. Second, when we finish, please keep the window open for a brief moment while I compile the session data. Would you like to begin?",
];

// Add new interfaces for Audio Context
interface AudioContextWithFlag extends AudioContext {
  isInitialized?: boolean;
}

interface InterviewerRespone {
  interviewerResponse: string;
  interviewerSession: InterviewSession;
  graphs?: string[] | null;
  goToNextQuestion: boolean;
  isEnd: boolean;
  tips: string;
  settIsEndHandler: (val: boolean) => void;
  isFirstTimeUser: boolean;
  questionTypes: string[];
  fallbackInformation?: string[];
}

interface InterviewContextType {
  caseQuestionNumber: number;
  currentQuestionNumber: number;
  isInterviewerSpeaking: boolean;
  isEnd: boolean;
  sessionId: string;
  graphs: string[];
  isFirstTimeUser: boolean;
  questionTypes: string[];
  tips: string;
  fallbackInformation: string[];
  fallbackInformationQuestionNumber: number;
  resetInterview: () => void;
  startInterviewHandler: (
    uid: string,
    pid: number,
    forceStartInterview: boolean,
    isWeeklyContest: boolean,
    contestId: string
  ) => Promise<string | { error: string }>;
  endInterviewHandler: (uid: string) => Promise<string | null>;
  quitInterviewHandler: () => void;
  finishAnsweringHandler: (skipThisQuestion?: boolean) => void;
  settIsEndHandler: (val: boolean) => void;
  setTipsHandler: (val: string) => void;
  playAudioByContent: (
    content: string | null,
    isTemporary?: boolean,
    shouldStartRecordingAfter?: boolean
  ) => void;
  stopAudio: () => void;
  checkInterviewCredits: (uid: string) => Promise<boolean>;
  audioRecordingUrl: string | null;
}

export const InterviewContext = createContext<InterviewContextType | undefined>(
  undefined
);

export const InterviewProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { firebaseUser } = useAuth();
  const { stream } = useMediaPermission();
  const [isInterviewerSpeaking, setIsInterviewerSpeaking] = useState(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(true);
  const [questionTypes, setQuestionTypes] = useState<string[]>([]);
  const [caseQuestionNumber, setCaseQuestionNumber] = useState(0);
  const [currentQuestionNumber, setCurrentQuestionNumber] = useState(0);
  const [sessionId, setSessionId] = useState("");
  const [graphs, setGraphs] = useState<string[]>([]);
  const [isEnd, setIsEnd] = useState(false);
  const isEndRef = useRef(false);
  const [tips, setTips] = useState<string>("");
  const [fallbackInformation, setFallbackInformation] = useState<string[]>([]);
  const [
    fallbackInformationQuestionNumber,
    setFallbackInformationQuestionNumber,
  ] = useState<number>(-1);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Audio recording related states
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null
  );
  const [audioChunks, setAudioChunks] = useState<BlobPart[]>([]);
  const [audioRecordingUrl, setAudioRecordingUrl] = useState<string | null>(
    null
  );
  const [shouldStartRecording, setShouldStartRecording] = useState(false);
  const [recordingStarted, setRecordingStarted] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(false);

  // Add Web Audio API related references
  const audioContextRef = useRef<AudioContextWithFlag | null>(null);
  const audioDestinationRef = useRef<MediaStreamAudioDestinationNode | null>(
    null
  );
  const microphoneSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const audioElementSourceRef = useRef<MediaElementAudioSourceNode | null>(
    null
  );
  const combinedStreamRef = useRef<MediaStream | null>(null);
  // Data request interval reference for MediaRecorder
  const dataRequestIntervalRef = useRef<number | null>(null);

  // Flag to indicate when quit/end process is active to prevent new audio playback
  const [isQuitting, setIsQuitting] = useState(false);
  // AbortController for canceling fetch operations
  const abortControllerRef = useRef<AbortController | null>(null);

  // Function to connect audio element to the audio graph
  const connectAudioElement = (audioElement: HTMLAudioElement) => {
    if (!audioContextRef.current || !audioDestinationRef.current) {
      console.error(
        "Cannot connect audio element: Audio context not initialized"
      );
      return false;
    }

    try {
      // Create source from audio element
      audioElementSourceRef.current =
        audioContextRef.current.createMediaElementSource(audioElement);

      // Connect to both the destination node (for recording) and the audio context destination (for playback)
      audioElementSourceRef.current.connect(audioDestinationRef.current);
      audioElementSourceRef.current.connect(
        audioContextRef.current.destination
      );

      console.log(
        "Audio element connected to audio graph for recording and playback"
      );
      return true;
    } catch (err) {
      console.error("Failed to connect audio element:", err);
      return false;
    }
  };

  // Function to clean up audio context connections
  const cleanupAudioGraph = () => {
    try {
      // Disconnect microphone if exists
      if (microphoneSourceRef.current) {
        microphoneSourceRef.current.disconnect();
        microphoneSourceRef.current = null;
      }

      // Disconnect audio element if exists
      if (audioElementSourceRef.current) {
        audioElementSourceRef.current.disconnect();
        audioElementSourceRef.current = null;
      }

      // Clear combined stream
      combinedStreamRef.current = null;

      console.log("Audio graph connections cleaned up");
    } catch (err) {
      console.error("Error cleaning up audio graph:", err);
    }
  };

  // Enhanced function to start recording both microphone and system audio if possible
  const startRecording = () => {
    if (!stream) {
      console.log(
        "Stream not available yet, setting flag to start recording when it becomes available"
      );
      setShouldStartRecording(true);
      return;
    }

    // Verify that the stream has audio tracks and they are active
    const audioTracks = stream.getAudioTracks();
    if (!audioTracks || audioTracks.length === 0) {
      console.error("Stream does not contain any audio tracks");
      return;
    }

    // Additional check to ensure audio tracks are active
    const activeAudioTracks = audioTracks.filter(
      (track) => track.enabled && track.readyState === "live"
    );
    console.log(
      `Stream has ${audioTracks.length} audio tracks, ${activeAudioTracks.length} are active`
    );

    if (activeAudioTracks.length === 0) {
      console.error("No active audio tracks found in stream");
      // Try to re-request permissions if no active tracks
      try {
        // Request media permissions directly here as a last resort
        console.log("Attempting to request fresh audio permissions...");
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then((freshStream) => {
            console.log(
              "Successfully got fresh audio stream, retrying recording"
            );
            // Update stream in MediaPermissionContext should happen automatically
            // Try recording again after a brief delay
            setTimeout(() => {
              setShouldStartRecording(true);
            }, 500);
          })
          .catch((err) => {
            console.error("Failed to get fresh audio permissions:", err);
          });
        return;
      } catch (err) {
        console.error("Error requesting fresh audio permissions:", err);
        return;
      }
    }

    // Clear any previous recording data
    setAudioChunks([]);
    setAudioRecordingUrl(null);

    try {
      // Clean up any existing MediaRecorder before creating a new one
      if (mediaRecorder) {
        try {
          if (mediaRecorder.state === "recording") {
            mediaRecorder.stop();
          }
        } catch (err) {
          console.warn("Error cleaning up previous MediaRecorder:", err);
        }
      }

      // Always prioritize simple microphone recording as primary approach for reliability
      // The Web Audio API mixing approach was causing empty chunks
      let streamToRecord = stream;

      // Make a clean clone of the stream
      const cleanStream = new MediaStream();
      streamToRecord.getAudioTracks().forEach((track) => {
        cleanStream.addTrack(track.clone());
      });

      // Get the preferred MIME type
      const mimeType = getMimeType();

      // Configure the recorder with optimized settings
      const options: MediaRecorderOptions = {};
      if (mimeType) {
        options.mimeType = mimeType;
      }
      options.audioBitsPerSecond = 128000;

      // Create a new MediaRecorder with our optimized settings
      console.log("Creating MediaRecorder with options:", options);
      const recorder = new MediaRecorder(cleanStream, options);

      // Important: Initialize array to store chunks in component state
      setAudioChunks([]);
      let chunkCount = 0;

      // Event handler when data is available
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunkCount++;
          // console.log(
          //     `Received chunk ${chunkCount}: ${event.data.size} bytes`
          // );
          setAudioChunks((chunks) => [...chunks, event.data]);
        } else {
          console.warn("Received empty data chunk, ignoring");
        }
      };

      // Add error handler
      recorder.onerror = (event) => {
        console.error("MediaRecorder error:", event);
        fallbackRecording(cleanStream);
      };

      // Start recording with a shorter timeslice for more frequent chunks
      console.log("Starting MediaRecorder...");
      try {
        recorder.start(500);
        console.log("MediaRecorder started successfully");
      } catch (startError) {
        console.error("Error starting MediaRecorder:", startError);
        return fallbackRecording(cleanStream);
      }

      // Set up a data request interval for more reliable chunk collection
      if (dataRequestIntervalRef.current) {
        clearInterval(dataRequestIntervalRef.current);
      }

      dataRequestIntervalRef.current = window.setInterval(() => {
        if (recorder && recorder.state === "recording") {
          try {
            recorder.requestData();

            // Log the current number of chunks periodically
            console.log(`Current audio chunks: ${chunkCount}`);

            // If we haven't received any chunks after a timeout, try fallback
            if (chunkCount === 0) {
              const elapsedTime = Date.now() - startTime;
              if (elapsedTime > 3000) {
                console.warn(
                  "No chunks received after 3 seconds, trying fallback"
                );
                clearInterval(dataRequestIntervalRef.current!);
                dataRequestIntervalRef.current = null;

                try {
                  recorder.stop();
                } catch (e) {
                  console.warn("Error stopping recorder:", e);
                }

                fallbackRecording(cleanStream);
              }
            }
          } catch (e) {
            console.warn("Error requesting data:", e);
          }
        }
      }, 2000);

      const startTime = Date.now();
      setMediaRecorder(recorder);
      setShouldStartRecording(false);
      setRecordingStarted(true);
      console.log("Recording started for session:", sessionId);
    } catch (err) {
      console.error("Error starting recorder:", err);
      fallbackRecording(stream);
    }
  };

  // Fallback recording with simpler approach
  const fallbackRecording = (audioStream: MediaStream) => {
    try {
      console.log("Using fallback recording approach...");

      // Clean up any ongoing recording
      cleanupAudioGraph();
      setAudioChunks([]);

      // Create a fresh stream with only audio tracks
      const freshStream = new MediaStream();
      audioStream.getAudioTracks().forEach((track) => {
        freshStream.addTrack(track.clone());
      });

      // Create recorder with minimal options
      const fallbackRecorder = new MediaRecorder(freshStream);
      let fallbackChunkCount = 0;

      fallbackRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          fallbackChunkCount++;
          console.log(
            `Fallback chunk ${fallbackChunkCount}: ${event.data.size} bytes`
          );
          setAudioChunks((chunks) => [...chunks, event.data]);
        }
      };

      fallbackRecorder.onerror = (event) => {
        console.error("Fallback recorder error:", event);
      };

      // Use larger time slices for more stable recording
      fallbackRecorder.start(1000);
      console.log("Fallback recorder started");

      // Request data periodically
      if (dataRequestIntervalRef.current) {
        clearInterval(dataRequestIntervalRef.current);
      }

      dataRequestIntervalRef.current = window.setInterval(() => {
        if (fallbackRecorder && fallbackRecorder.state === "recording") {
          try {
            fallbackRecorder.requestData();
            console.log(`Current fallback chunks: ${fallbackChunkCount}`);
          } catch (e) {
            console.warn("Error requesting fallback data:", e);
          }
        }
      }, 2000);

      setMediaRecorder(fallbackRecorder);
      setRecordingStarted(true);
      setShouldStartRecording(false);
    } catch (fallbackErr) {
      console.error("All recording approaches failed:", fallbackErr);
      setShouldStartRecording(false);
      alert(
        "Unable to record audio. Please check your microphone permissions and try again."
      );
    }
  };

  // Get supported MIME type function
  const getMimeType = () => {
    // Try each MIME type in order of preference
    const types = [
      "audio/wav", // Try wav first as it's most universally compatible
      "audio/webm", // Basic webm without codec specification
      "audio/mp4", // MP4 fallback
      "", // Let browser decide as last resort
    ];

    for (const type of types) {
      if (type === "" || MediaRecorder.isTypeSupported(type)) {
        console.log("Using MIME type:", type || "browser default");
        return type;
      }
    }

    return ""; // Let the browser decide as fallback
  };

  // Function to stop recording without saving
  const stopRecordingWithoutSaving = () => {
    setShouldStartRecording(false);

    // Clear any data request interval
    if (dataRequestIntervalRef.current) {
      clearInterval(dataRequestIntervalRef.current);
      dataRequestIntervalRef.current = null;
    }

    if (mediaRecorder && mediaRecorder.state === "recording") {
      try {
        // Stop the recorder but don't process the data
        mediaRecorder.onstop = () => {
          console.log("Recording stopped (without saving)");
          setAudioChunks([]); // Clear the chunks
          setRecordingStarted(false); // Mark that recording has stopped

          // Clean up audio graph connections
          cleanupAudioGraph();
        };

        mediaRecorder.stop();
        setMediaRecorder(null);
      } catch (err) {
        console.error("Error stopping recorder:", err);
        setRecordingStarted(false); // Still mark as stopped even if there was an error
        cleanupAudioGraph(); // Still try to clean up
      }
    } else {
      console.log("No active recorder to stop");
      setRecordingStarted(false); // Ensure recording is marked as stopped
      cleanupAudioGraph(); // Still clean up any connections
    }
  };

  // Function to collect all audio chunks and return as a blob
  const collectAudioBlob = (): { blob: Blob | null; mimeType: string } => {
    if (audioChunks.length === 0) {
      console.warn("No audio chunks available to create blob");
      return { blob: null, mimeType: "audio/webm" };
    }

    // Determine the best mime type to use
    let mimeType = "audio/webm";

    // If we have a recorder, use its mime type
    if (
      mediaRecorder &&
      mediaRecorder.mimeType &&
      mediaRecorder.mimeType !== ""
    ) {
      mimeType = mediaRecorder.mimeType;
    }

    // Make a copy of the chunks to prevent race conditions
    const chunksToProcess = [...audioChunks];
    console.log(`Processing ${chunksToProcess.length} audio chunks`);

    // Filter out any empty chunks
    const validChunks = chunksToProcess.filter((chunk) => {
      const blobChunk = chunk instanceof Blob ? chunk : new Blob([chunk]);
      return blobChunk.size > 0;
    });

    if (validChunks.length === 0) {
      console.warn("No valid audio chunks after filtering");
      return { blob: null, mimeType };
    }

    try {
      // Create blob with explicit type - handle differently based on browser
      // Create separate blobs and merge them to avoid encoding issues
      const audioBlob = new Blob(validChunks, { type: mimeType });

      console.log("Created audio blob:", {
        size: audioBlob.size,
        type: audioBlob.type,
        chunksCount: validChunks.length,
      });

      // Validate blob size
      if (audioBlob.size < 1000) {
        console.warn("Audio blob is too small, probably invalid");
        return { blob: null, mimeType };
      }

      return { blob: audioBlob, mimeType };
    } catch (error) {
      console.error("Error creating audio blob:", error);
      return { blob: null, mimeType };
    }
  };

  // Function to upload audio to Firebase Storage via Cloud Function
  const uploadAudioToStorage = async (
    sid: string,
    questionIndex: number
  ): Promise<boolean> => {
    // Collect the audio blob from chunks
    const { blob, mimeType } = collectAudioBlob();

    if (!blob || blob.size < 1000) {
      console.warn("Audio blob is too small or doesn't exist, skipping upload");
      return false;
    }

    try {
      // Calculate audio duration
      const audioDuration = await getAudioDuration(blob);

      console.log(
        `Starting upload for session ${sid}, question ${questionIndex}, blob size: ${
          blob.size
        } bytes, duration: ${audioDuration.toFixed(2)}s, mime type: ${mimeType}`
      );

      // Call the Cloud Function to upload the audio
      const uploadInterviewRecording = httpsCallable(
        functions,
        "uploadInterviewRecording"
      );

      // Convert Blob to Base64 for transfer
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = async () => {
          try {
            const base64data = reader.result as string;
            // Remove the data URL prefix (e.g., "data:audio/webm;base64,")
            const base64Audio = base64data.split(",")[1];

            console.log(
              `Converted audio to base64, length: ${
                base64Audio.length
              }, data URL prefix: ${base64data.substring(0, 50)}...`
            );

            // Simplified format extraction to avoid codec issues
            const format = mimeType.split("/")[1]?.split(";")[0] || "webm";

            // Call the cloud function with the audio data and session info
            const result = await uploadInterviewRecording({
              sessionId: sid,
              audioData: base64Audio,
              mimeType: mimeType,
              questionIndex: questionIndex,
              duration: audioDuration,
              format: format,
            });

            console.log("Upload result:", result.data);
            resolve(true);
          } catch (error) {
            console.error("Error during upload process:", error);
            resolve(false);
          }
        };

        reader.onerror = () => {
          console.error("Error reading audio blob");
          resolve(false);
        };
      });
    } catch (err) {
      console.error("Error uploading to Firebase:", err);
      return false;
    }
  };

  // Cleanup function for local audio URLs
  const cleanupAudioUrl = (url: string | null) => {
    if (url) {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        console.warn("Error revoking object URL:", e);
      }
    }
  };

  // Function to both save locally and upload to Firebase
  const stopRecordingAndSaveWithUpload = async (
    sid: string,
    questionIndex: number
  ): Promise<string | null> => {
    setShouldStartRecording(false);
    setPendingUpload(true);

    // Clear any previous URL to prevent memory leaks
    if (audioRecordingUrl) {
      cleanupAudioUrl(audioRecordingUrl);
    }

    console.log(
      `Stopping recording for session ${sid}, question ${questionIndex}, recorder state: ${
        mediaRecorder?.state || "no recorder"
      }`
    );

    // Create a variable to store the local URL
    let localAudioUrl: string | null = null;

    // If no active recorder but we have chunks, try to process them
    if (!mediaRecorder || mediaRecorder.state === "inactive") {
      console.log("No active recorder, but processing existing chunks");
      setRecordingStarted(false);

      // Create the local URL from existing chunks
      const { blob } = collectAudioBlob();
      if (blob && blob.size > 1000) {
        localAudioUrl = URL.createObjectURL(blob);
        setAudioRecordingUrl(localAudioUrl);

        // Try to upload to Firebase in the background
        uploadAudioToStorage(sid, questionIndex)
          .then((success) => {
            console.log(
              `Background upload ${success ? "succeeded" : "failed"}`
            );
            setPendingUpload(false);
          })
          .catch((err) => {
            console.error("Error in background upload:", err);
            setPendingUpload(false);
          });

        // Clean up audio graph
        cleanupAudioGraph();

        return localAudioUrl;
      }

      setPendingUpload(false);
      cleanupAudioGraph();
      return null;
    }

    // If we have an active recorder, stop it and process the data
    return new Promise<string | null>((resolve) => {
      // Make sure any data request interval is cleared
      if (dataRequestIntervalRef.current) {
        clearInterval(dataRequestIntervalRef.current);
        dataRequestIntervalRef.current = null;
      }

      // First request any pending data
      if (mediaRecorder.state === "recording") {
        try {
          // Request data one final time to ensure we get the latest audio
          mediaRecorder.requestData();

          // Wait for any final chunks to be processed
          setTimeout(() => {
            try {
              mediaRecorder.onstop = async () => {
                setRecordingStarted(false);
                console.log("Recorder stopped, processing audio");

                // Log the number of chunks we have
                console.log(
                  `Processing ${audioChunks.length} audio chunks after stopping`
                );

                // First create a local URL for immediate access
                const { blob } = collectAudioBlob();
                if (blob && blob.size > 1000) {
                  localAudioUrl = URL.createObjectURL(blob);
                  setAudioRecordingUrl(localAudioUrl);

                  // Upload to Firebase in the background
                  uploadAudioToStorage(sid, questionIndex)
                    .then((success) => {
                      console.log(
                        `Background upload ${success ? "succeeded" : "failed"}`
                      );
                      setPendingUpload(false);
                    })
                    .catch((err) => {
                      console.error("Error in background upload:", err);
                      setPendingUpload(false);
                    });

                  // Clean up audio graph
                  cleanupAudioGraph();

                  resolve(localAudioUrl);
                } else {
                  console.warn("Audio blob is too small or doesn't exist");
                  setPendingUpload(false);
                  cleanupAudioGraph();
                  resolve(null);
                }
              };

              // Stop the recorder after ensuring onstop handler is set
              mediaRecorder.stop();
              setMediaRecorder(null);
            } catch (stopErr) {
              console.error("Error stopping recorder:", stopErr);
              handleRecordingError();
            }
          }, 500); // Increased delay to ensure chunks are processed
        } catch (err) {
          console.error("Error requesting final data chunk:", err);
          handleRecordingError();
        }
      } else {
        handleRecordingError();
      }

      function handleRecordingError() {
        // Error handling if stopping fails
        setRecordingStarted(false);
        console.log("Manually processing audio after stop error");

        // Process the audio chunks since we couldn't properly stop the recorder
        const { blob } = collectAudioBlob();
        if (blob && blob.size > 1000) {
          localAudioUrl = URL.createObjectURL(blob);
          setAudioRecordingUrl(localAudioUrl);

          // Start the upload process
          uploadAudioToStorage(sid, questionIndex)
            .then((success) => {
              console.log(
                `Background upload ${success ? "succeeded" : "failed"}`
              );
              setPendingUpload(false);
            })
            .catch((err) => {
              console.error("Error in background upload:", err);
              setPendingUpload(false);
            });

          // Clean up audio graph
          cleanupAudioGraph();

          resolve(localAudioUrl);
        } else {
          console.warn("Audio blob is too small or doesn't exist after error");
          setPendingUpload(false);
          cleanupAudioGraph();
          resolve(null);
        }
      }
    });
  };

  // Check if user has enough interview credits
  const checkInterviewCredits = async (uid: string): Promise<boolean> => {
    try {
      // Instead of calling a Cloud Function, let's check the user document directly
      const db = getFirestore();
      const userDocRef = doc(db, "users", uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        console.error("User document not found");
        return false;
      }

      const userData = userDocSnap.data();

      // Check free interviews
      let totalInterviews = userData.interviewCount || 0;

      // Check bought interviews
      const now = new Date();
      const boughtInterviews = userData.boughtInterviews || [];

      // Sum up non-expired bought interviews
      for (const item of boughtInterviews) {
        const expiresAt = item.expiresAt?._seconds
          ? new Date(item.expiresAt._seconds * 1000)
          : null;

        if (!expiresAt || expiresAt > now) {
          totalInterviews += item.count || 0;
        }
      }
      console.log("totalInterviews", totalInterviews);
      // User has enough credits if total is greater than 0
      return totalInterviews > 0;
    } catch (error) {
      console.error("Error checking interview credits:", error);
      return false;
    }
  };

  const startInterviewHandler = async (
    uid: string,
    pid: number,
    forceStartInterview: boolean = false,
    isWeeklyContest: boolean = false,
    contestId: string = ""
  ): Promise<string | { error: string }> => {
    // console.log("startInterviewHandler called");
    let userId = uid;
    if (!uid || uid.length === 0) {
      userId = firebaseUser?.uid || "";
    }

    try {
      // Check if user has enough credits to start an interview
      const hasEnoughCredits = await checkInterviewCredits(userId);
      console.log("hasEnoughCredits", hasEnoughCredits);
      if (!hasEnoughCredits && !isWeeklyContest) {
        console.error("Not enough interview credits");
        return {
          error:
            "You don't have enough interview credits. Purchase more to continue.",
        };
      }

      localStorage.setItem("lastCaseInterview", pid.toString());
      const startInterview = httpsCallable(functions, "startInterview");
      await new Promise((resolve) => setTimeout(resolve, 1200));
      const result = (await startInterview({
        uid: userId,
        pid,
        forceStartInterview,
        isWeeklyContest,
        contestId,
      })) as {
        data: {
          sessionId: string;
          caseQuestionNumber: number;
          currentQuestionNumber: number;
          isFirstTimeUser: boolean;
          questionTypes: string[];
          wordBoosts: string[];
          fallbackInformation?: string[];
        };
      };
      const {
        caseQuestionNumber,
        currentQuestionNumber,
        sessionId,
        isFirstTimeUser,
        questionTypes,
        wordBoosts,
        fallbackInformation,
      } = result.data;
      console.log("result.data", result.data);
      //cold start, initialize the numbers
      setGraphs([]);
      setIsEnd(false);
      setSessionId(sessionId);
      setCaseQuestionNumber(caseQuestionNumber);
      setCurrentQuestionNumber(currentQuestionNumber);
      setIsFirstTimeUser(isFirstTimeUser);
      setQuestionTypes(["prompt", ...questionTypes]);
      console.log("startInterviewHandler", result.data);

      // Handle fallback information from start interview if available
      if (
        fallbackInformation !== null &&
        fallbackInformation !== undefined &&
        Array.isArray(fallbackInformation) &&
        fallbackInformation.length > 0
      ) {
        console.log(
          "Setting fallback information from start interview:",
          fallbackInformation
        );
        setFallbackInformation(fallbackInformation);
        setFallbackInformationQuestionNumber(0); // Set to 0 for the prompt phase
      } else {
        // Clear any existing fallback information
        setFallbackInformation([]);
        setFallbackInformationQuestionNumber(-1);
      }

      // IMPORTANT: explicitly set shouldStartRecording to false to prevent automatic recording
      setShouldStartRecording(false);

      // Clear any old recording data
      setAudioChunks([]);
      setAudioRecordingUrl(null);

      if (!isFirstTimeUser) {
        // Pass true as the third parameter to indicate we should start recording after audio
        playAudioByContent(
          getRandomItem(WELCOME_MESSAGES),
          false,
          true // Indicate this is start of interview
        );
        setTimeout(async () => {
          await startTranscription(30_000, wordBoosts);
        }, 10_000);
      }

      // If everything worked, return sessionId
      return sessionId;
    } catch (error) {
      console.error("Error starting interview:", error);
      // Return error object
      return { error: "Failed to start interview. Please try again." };
    }
  };

  const settIsEndHandler = (val: boolean) => {
    setIsEnd(val);
    isEndRef.current = val;
    setGraphs([]);

    // If setting isEnd to true, we should prepare to upload the recording
    if (val === true && recordingStarted) {
      console.log("Interview is ending, preparing to save recording");
    }
  };

  const setTipsHandler = (val: string) => {
    setTips(val);
  };

  const cleanupMediaTracks = () => {
    // Clean up all media tracks
    if (stream) {
      stream.getTracks().forEach((track) => {
        console.log(`Stopping media track: ${track.kind}`);
        track.stop();
      });
    }

    // Clean up any combined stream
    if (combinedStreamRef.current) {
      combinedStreamRef.current.getTracks().forEach((track) => {
        console.log(`Stopping combined stream track: ${track.kind}`);
        track.stop();
      });
      combinedStreamRef.current = null;
    }
  };

  const endInterviewHandler = throttle(async (uid: string) => {
    try {
      console.log("End interview handler called");

      // Set both state and ref immediately
      setIsEnd(true);
      isEndRef.current = true;
      setShouldStartRecording(false);

      // Stop any playing audio first
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          const oldAudio = audioRef.current;
          audioRef.current = null;
          oldAudio.removeAttribute("src");
          if (oldAudio.src && oldAudio.src.startsWith("blob:")) {
            URL.revokeObjectURL(oldAudio.src);
          }
        } catch (err) {
          console.warn("Error stopping audio during end interview:", err);
        }
      }

      // Clear any data request interval
      if (dataRequestIntervalRef.current) {
        clearInterval(dataRequestIntervalRef.current);
        dataRequestIntervalRef.current = null;
      }

      const endInterview = httpsCallable(functions, "endInterview");
      const result = await endInterview({ uid, sessionId });

      // const generateReport = async (sessionId: string, uid: string) => {
      //     try {
      //         // Call the Cloud Function to initiate report generation
      //         const initiateReport = httpsCallable(
      //             functions,
      //             "initiateReportGeneration"
      //         );
      //         const result = await initiateReport({
      //             sessionId: sessionId.trim(),
      //             uid: uid.trim(),
      //         });

      //         const data = result.data as {
      //             success: boolean;
      //             requestId: string;
      //             message: string;
      //         };

      //         if (data.success) {
      //             console.log(
      //                 `Report generation initiated: ${data.message}`
      //             );
      //             console.log(`Request ID: ${data.requestId}`);

      //             // Optionally listen for updates on the request document
      //             // This allows showing real-time status updates to the user
      //             return {
      //                 success: true,
      //                 requestId: data.requestId,
      //                 message: data.message,
      //             };
      //         } else {
      //             console.error("Failed to initiate report generation");
      //             return {
      //                 success: false,
      //                 message: "Failed to initiate report generation",
      //             };
      //         }
      //     } catch (error) {
      //         console.error(
      //             "Error calling report generation function:",
      //             error
      //         );
      //         return {
      //             success: false,
      //         };
      //     }
      // };
      // const reportResult = await generateReport(sessionId, uid);
      // console.log("reportResult", reportResult);
      const data = result.data as InterviewSession;
      console.log("endInterviewHandler result:", data);

      // Wait until isInterviewerSpeaking flips to false
      await closeTranscription();
      await new Promise<void>((resolve) => setTimeout(resolve, 1000));
      await waitForInterviewerToFinish();

      // Get the final session ID to use for the recording
      const finalSessionId = data.sessionId || sessionId;
      console.log(`Ending interview for session ${finalSessionId}`);

      // Check if we've started recording at all
      if (recordingStarted || audioChunks.length > 0) {
        console.log("Recording in progress, stopping and saving with upload");

        // Use combined function that saves locally and uploads to Firebase
        // For the final recording, use the current question number
        const url = await stopRecordingAndSaveWithUpload(
          finalSessionId,
          currentQuestionNumber
        );

        if (url) {
          console.log(
            "Successfully saved interview recording locally and started Firebase upload"
          );
        } else {
          console.warn("Failed to save interview recording");
        }
      } else {
        console.log("No recording was started for this session");
      }

      // Clear recording-related state
      setRecordingStarted(false);
      setShouldStartRecording(false);

      // Ensure all audio graph connections are cleaned up
      cleanupAudioGraph();

      // Clean up media tracks
      cleanupMediaTracks();

      resetInterview();
      setIsInterviewerSpeaking(false);
      return finalSessionId;
    } catch (error) {
      console.error("Error ending interview:", error);

      // Still try to stop recording even if ending interview failed
      if (recordingStarted || audioChunks.length > 0) {
        await stopRecordingAndSaveWithUpload(sessionId, currentQuestionNumber);
      }

      // Clear recording state even if there was an error
      setRecordingStarted(false);
      setShouldStartRecording(false);
      cleanupAudioGraph();
      cleanupMediaTracks();
      setIsInterviewerSpeaking(false);

      return null;
    }
  }, 5000);

  const quitInterviewHandler = throttle(async () => {
    console.log("Quit interview handler called");

    // Set quitting flag immediately to block any new audio from starting
    setIsQuitting(true);

    // Create and set abort controller to cancel any pending fetch operations
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // First stop any playing audio immediately
    if (audioRef.current) {
      try {
        console.log("Stopping audio during quit");
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        // Clear the reference immediately to prevent replay
        const oldAudio = audioRef.current;
        audioRef.current = null;
        // Remove src attribute
        oldAudio.removeAttribute("src");
        // Release object URL if it exists
        if (oldAudio.src && oldAudio.src.startsWith("blob:")) {
          URL.revokeObjectURL(oldAudio.src);
        }
      } catch (err) {
        console.warn("Error stopping audio during quit:", err);
      }
    }

    // Clear any data request interval
    if (dataRequestIntervalRef.current) {
      clearInterval(dataRequestIntervalRef.current);
      dataRequestIntervalRef.current = null;
    }

    // Force stop MediaRecorder if it exists and is recording
    if (mediaRecorder && mediaRecorder.state === "recording") {
      try {
        mediaRecorder.stop();
      } catch (err) {
        console.warn("Error stopping MediaRecorder:", err);
      }
      setMediaRecorder(null);
    }

    // Clear all audio chunks and recording state
    setAudioChunks([]);
    setRecordingStarted(false);
    setShouldStartRecording(false);
    setAudioRecordingUrl(null);
    setPendingUpload(false);

    // Clean up audio graph connections
    cleanupAudioGraph();

    // Clean up media tracks
    cleanupMediaTracks();

    // Close transcription and reset state
    await closeTranscription();
    resetInterview();

    // Ensure interviewer speaking state is reset
    setIsInterviewerSpeaking(false);
  }, 3000);

  function waitForInterviewerToFinish(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (!isInterviewerSpeaking) {
        resolve();
        return;
      }
      // Poll every 100 ms, or 200 ms, etc.
      const interval = setInterval(() => {
        // If the state flipped to false, resolve and clear
        if (!isInterviewerSpeaking) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
    });
  }
  const userInputHandler = throttle(
    async (
      userInput: string,
      pauseTranscription: (duration?: number) => void,
      skipProcess: boolean = false,
      skipThisQuestion: boolean = false
    ) => {
      console.log(
        "userInputHandler called with skipThisQuestion:",
        skipThisQuestion
      );
      let result: any;

      // Retry configuration
      const MAX_RETRIES = 3;
      let retryCount = 0;
      let retryDelay = 1000; // Start with 1 second delay, will increase exponentially

      if (skipThisQuestion) {
        console.log("Processing skip question request");
        const processUserInput = httpsCallable(functions, "processUserInput");

        // Add retry logic with exponential backoff for skip question scenario
        while (retryCount < MAX_RETRIES) {
          try {
            result = await processUserInput({
              uid: firebaseUser?.uid,
              userInput:
                "I don't know how to answer this question, let's skip this question and move to the next one.",
              skipThisQuestion: true,
            });
            break; // Success, exit the retry loop
          } catch (error) {
            retryCount++;
            console.error(
              `Error processing skip request (attempt ${retryCount}/${MAX_RETRIES}):`,
              error
            );

            if (retryCount >= MAX_RETRIES) {
              console.error(
                "Max retries reached for skip request. Handling failure."
              );
              // Set a default result for when all retries fail
              result = {
                data: {
                  interviewerResponse:
                    "I'm having trouble connecting to the server. Let's try again. Please repeat your answer or try skipping again.",
                  goToNextQuestion: false,
                  isEnd: false,
                  tips: "There was a connection issue. You can try speaking again.",
                  graphs: [],
                  fallbackInformation: [],
                },
              };
              // Resume transcription to allow user to try again
              resumeTranscription();
              break;
            }

            // Wait before retrying with exponential backoff
            console.log(`Retrying in ${retryDelay}ms...`);
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
            retryDelay *= 2; // Exponential backoff
          }
        }
      } else if (!skipProcess) {
        const processUserInput = httpsCallable(functions, "processUserInput");

        // Add retry logic with exponential backoff for normal processing
        while (retryCount < MAX_RETRIES) {
          try {
            result = await processUserInput({
              uid: firebaseUser?.uid,
              userInput,
              skipThisQuestion: false,
            });
            break; // Success, exit the retry loop
          } catch (error) {
            retryCount++;
            console.error(
              `Error processing user input (attempt ${retryCount}/${MAX_RETRIES}):`,
              error
            );

            if (retryCount >= MAX_RETRIES) {
              console.error(
                "Max retries reached for user input. Handling failure."
              );
              // Set a default result for when all retries fail
              result = {
                data: {
                  interviewerResponse:
                    "I'm having trouble connecting to the server. Let's try again. Please repeat your answer.",
                  goToNextQuestion: false,
                  isEnd: false,
                  tips: "There was a connection issue. You can try speaking again.",
                  graphs: [],
                },
              };
              // Resume transcription to allow user to try again
              resumeTranscription();
              break;
            }

            // Wait before retrying with exponential backoff
            console.log(`Retrying in ${retryDelay}ms...`);
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
            retryDelay *= 2; // Exponential backoff
          }
        }
      } else {
        result = {
          data: {
            interviewerResponse: "Sorry, I didn't catch that.",
            goToNextQuestion: false,
            isEnd: false,
            tips: "",
            graphs: [],
          },
        };
      }

      pauseTranscription();

      const resultData = result.data as InterviewerRespone;
      if (!resultData.interviewerResponse) {
        console.log("No response from interviewer");
        // If no response, allow user to try again
        resumeTranscription();
        return;
      }
      console.log("resultData from processUserInput:", resultData);

      // Handle fallback information based on whether we're moving to next question
      if (resultData.goToNextQuestion || skipThisQuestion) {
        // When moving to next question, clear fallback info unless new info is provided
        if (
          resultData.fallbackInformation !== null &&
          resultData.fallbackInformation !== undefined &&
          Array.isArray(resultData.fallbackInformation) &&
          resultData.fallbackInformation.length > 0
        ) {
          console.log(
            "Moving to next question with new fallback information:",
            resultData.fallbackInformation
          );
          setFallbackInformation(resultData.fallbackInformation);
          setFallbackInformationQuestionNumber(currentQuestionNumber + 1);
        } else {
          // Clear fallback information when moving to next question without new info
          console.log("Moving to next question, clearing fallback information");
          setFallbackInformation([]);
          setFallbackInformationQuestionNumber(-1);
        }
      } else {
        // Not moving to next question, update fallback info for current question
        if (
          resultData.fallbackInformation !== null &&
          resultData.fallbackInformation !== undefined &&
          Array.isArray(resultData.fallbackInformation) &&
          resultData.fallbackInformation.length > 0
        ) {
          console.log(
            "Updating fallback information for current question:",
            resultData.fallbackInformation
          );
          setFallbackInformation(resultData.fallbackInformation);
          setFallbackInformationQuestionNumber(currentQuestionNumber);
        } else if (
          resultData.fallbackInformation === null ||
          resultData.fallbackInformation === undefined
        ) {
          // Clear fallback information if explicitly set to null/undefined
          console.log("Clearing fallback information for current question");
          setFallbackInformation([]);
          setFallbackInformationQuestionNumber(-1);
        }
        // If fallbackInformation is an empty array, we don't update it (keep existing)
      }

      // If we're moving to the next question, update the question number
      if (resultData.goToNextQuestion || skipThisQuestion) {
        // Update question number
        if (resultData.goToNextQuestion) {
          setCurrentQuestionNumber(currentQuestionNumber + 1);
        }
      }

      if (resultData.isEnd) {
        console.log("Server indicated interview is ending");
        setIsEnd(true);
      } else {
        setIsEnd(false);
      }

      if (Array.isArray(resultData.graphs) && resultData.graphs.length > 0) {
        setGraphs(resultData.graphs);
      } else {
        setGraphs([]);
      }

      if (resultData.tips) {
        setTips(resultData.tips);
      } else {
        setTips("");
      }

      console.log("Playing interviewer response");
      // The recording will be started in the onended callback of playAudioByContent
      await playAudioByContent(
        resultData.interviewerResponse,
        false,
        true // Always start recording after interviewer response
      );
    },
    2_000
  );
  const endInterviewHandlerRef = useRef(endInterviewHandler);

  const {
    startTranscription,
    closeTranscription,
    isRecording,
    pauseTranscription,
    forcePauseTranscription,
    resumeTranscription,
    getBufferedTranscription,
  } = useTranscription({
    onFinalTranscript: (text: string) => {
      userInputHandler(text, pauseTranscription);
    },
    playAudioByContent: (text: string) => {
      playAudioByContent(text);
    },
  });
  const isRecordingRef = useRef(isRecording);

  const finishAnsweringHandler = throttle(
    async (skipThisQuestion: boolean = false) => {
      console.log(
        "finishAnsweringHandler called, skipThisQuestion:",
        skipThisQuestion
      );
      // Now call forcePauseTranscription
      await forcePauseTranscription();

      // Save the current audio recording regardless of whether we're skipping or not
      if (recordingStarted || audioChunks.length > 0) {
        console.log("Saving current audio recording segment");

        // Stop recording and save the current audio
        await stopRecordingAndSaveWithUpload(sessionId, currentQuestionNumber);
      }

      if (skipThisQuestion) {
        console.log("Skipping question, bypassing transcript collection");
        await userInputHandler(
          "", // Empty transcript since we're skipping
          pauseTranscription,
          false, // Don't skip processing
          true // Mark as skipping this question
        );
        return;
      }

      // Not skipping, process normally
      await new Promise((resolve) => setTimeout(resolve, 1_100));
      let fetchedTranscript = "";
      let fetchedBufferLength = 0;
      let RETRY_LIMIT = 3;

      // When it happens with the end of socket
      while (fetchedTranscript.length === 0 && RETRY_LIMIT > 0) {
        console.log("Retrying to get final transcript...", RETRY_LIMIT);
        const { finalTranscript, bufferLength } = getBufferedTranscription();
        fetchedTranscript = finalTranscript;
        fetchedBufferLength = bufferLength;
        RETRY_LIMIT--;
      }

      if (fetchedTranscript.length === 0) {
        // NO TRANSCRIPT
        console.log("No transcript to send!");
        await userInputHandler("ugh, sorry", pauseTranscription, true, false);
        return;
      }

      console.log("Final transcript:", fetchedTranscript);
      console.log("Buffer length:", fetchedBufferLength);

      if (fetchedBufferLength > 2) {
        await playAudioByContent("okay..", true);
        if (fetchedBufferLength > 4)
          setTimeout(async () => {
            await playAudioByContent("Let me see..", true);
          }, 200);
      }

      await userInputHandler(
        fetchedTranscript,
        pauseTranscription,
        false,
        false
      );
      console.log("Finish handle user input!!!!!!");
    },
    2000
  ); // Throttle delay in ms (2 second)

  // Keep isRecordingRef updated
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  // Watch for stream availability and start recording if flag is set
  useEffect(() => {
    if (stream && shouldStartRecording) {
      console.log("Stream is now available, starting recording");
      // Add a delay to ensure the stream is fully initialized
      setTimeout(() => {
        startRecording();
      }, 300);
    }
  }, [stream, shouldStartRecording]);

  // Effect to handle when isEnd changes to true
  useEffect(() => {
    if (isEnd && recordingStarted && audioChunks.length > 0) {
      console.log(
        "Interview ended, saving recording before potential navigation"
      );
      // We'll trigger an immediate save and upload to ensure we capture the recording
      // even if the user navigates away soon after
      const saveSid = sessionId;
      const saveQuestionIndex = currentQuestionNumber;

      // Use setTimeout to ensure this runs after the current execution cycle
      setTimeout(() => {
        if (mediaRecorder && mediaRecorder.state === "recording") {
          // Use the combined function instead of just local save
          endInterviewHandlerRef
            .current(saveSid)
            .then((url) => {
              console.log(
                url
                  ? "Successfully saved and started upload of recording via isEnd effect"
                  : "Failed to save recording via isEnd effect"
              );
            })
            .catch((err) => {
              console.error("Error in isEnd recording handler:", err);
            });
        }
      }, 300);
    }
  }, [isEnd]);

  // Cleanup recording when component unmounts
  useEffect(() => {
    return () => {
      // Clear any active interval
      if (dataRequestIntervalRef.current) {
        clearInterval(dataRequestIntervalRef.current);
        dataRequestIntervalRef.current = null;
      }

      if (mediaRecorder && mediaRecorder.state === "recording") {
        stopRecordingWithoutSaving();
      }
      if (audioRecordingUrl) {
        URL.revokeObjectURL(audioRecordingUrl);
      }

      // Close the audio context
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
          audioContextRef.current = null;
        } catch (err) {
          console.error("Error closing audio context:", err);
        }
      }
    };
  }, [mediaRecorder, audioRecordingUrl]);

  const playAudioByContent = async (
    content: string | null,
    isTemporary: boolean = false,
    shouldStartRecordingAfter: boolean = false
  ) => {
    console.log("[InterviewContext] Starting playAudioByContent", {
      content,
      isTemporary,
      shouldStartRecordingAfter,
    });

    // Check both state and ref
    if (isQuitting || !content || isEnd || isEndRef.current) {
      console.log(
        "[InterviewContext] Skipping audio playback:",
        isQuitting
          ? "Ignoring audio request during quit"
          : isEnd || isEndRef.current
          ? "Ignoring audio request - interview has ended"
          : "No content to play audio"
      );
      return;
    }

    try {
      // Create abort controller for this request
      if (abortControllerRef.current) {
        console.log("[InterviewContext] Aborting existing audio operation");
        abortControllerRef.current.abort(); // Abort any existing operations
      }
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      // First stop any currently playing audio - ensure this is complete before proceeding
      if (audioRef.current) {
        try {
          console.log(
            "[InterviewContext] Stopping existing audio before playing new content"
          );
          audioRef.current.pause();
          audioRef.current.currentTime = 0;

          // Explicitly remove event listeners to prevent memory leaks and callbacks
          audioRef.current.oncanplaythrough = null;
          audioRef.current.onended = null;
          audioRef.current.onerror = null;

          const oldAudio = audioRef.current;
          audioRef.current = null;
          oldAudio.removeAttribute("src");
          if (oldAudio.src && oldAudio.src.startsWith("blob:")) {
            URL.revokeObjectURL(oldAudio.src);
          }

          console.log(
            "[InterviewContext] Adding forced delay after stopping audio"
          );
          // Add a forced delay to ensure audio has completely stopped
          await Promise.race([
            new Promise((resolve) => setTimeout(resolve, 100)),
            new Promise((_, reject) => {
              signal.addEventListener("abort", () =>
                reject(new Error("Aborted"))
              );
            }),
          ]);
        } catch (stopErr: any) {
          if (stopErr.message === "Aborted") {
            console.log(
              "[InterviewContext] Audio stop operation aborted during quit"
            );
            return;
          }
          console.warn(
            "[InterviewContext] Error stopping previous audio:",
            stopErr
          );
        }
      }

      // Check again if quitting flag is set after stopping audio
      if (isQuitting) {
        console.log(
          "[InterviewContext] Quitting detected after audio stop, aborting playback"
        );
        return;
      }

      // Mark that the interviewer is now speaking
      console.log("[InterviewContext] Setting isInterviewerSpeaking to true");
      setIsInterviewerSpeaking(true);

      // Get the audio from TTS API with abort signal
      let audioBlob;
      try {
        console.log("[InterviewContext] Fetching audio from TTS API");
        audioBlob = await gcpTextToSpeech(
          content,
          process.env.REACT_APP_GCP_TTS_API_KEY || ""
        );
        console.log(
          "[InterviewContext] Successfully received audio blob from TTS API"
        );

        // Check for quit right after fetch even without abort signal
        if (isQuitting) {
          console.log(
            "[InterviewContext] Quitting detected after TTS fetch, aborting playback"
          );
          return;
        }
      } catch (fetchErr: any) {
        if (fetchErr.name === "AbortError") {
          console.log("[InterviewContext] TTS fetch aborted during quit");
          return;
        }
        throw fetchErr;
      }

      // Create URL for the audio blob
      const audioUrl = URL.createObjectURL(audioBlob);
      console.log("[InterviewContext] Created audio URL from blob");

      // Check for quit after URL creation
      if (isQuitting) {
        URL.revokeObjectURL(audioUrl);
        console.log(
          "[InterviewContext] Quitting detected after URL creation, aborting playback"
        );
        return;
      }

      // Create new audio element
      const newAudio = new Audio();
      console.log("[InterviewContext] Created new Audio element");

      // Add event listeners before setting src
      newAudio.oncanplaythrough = () => {
        console.log("[InterviewContext] Audio canplaythrough event fired");
        // Final check before play
        if (isQuitting) {
          console.log(
            "[InterviewContext] Quitting detected in canplaythrough, aborting playback"
          );
          URL.revokeObjectURL(audioUrl);
          return;
        }

        try {
          // Try to connect the audio element to the audio graph for recording
          if (recordingStarted && audioContextRef.current?.isInitialized) {
            try {
              connectAudioElement(newAudio);
              console.log(
                "[InterviewContext] Connected audio to recording graph"
              );
            } catch (connectErr) {
              console.error(
                "[InterviewContext] Couldn't connect audio element to recording:",
                connectErr
              );
            }
          }

          console.log(
            "[InterviewContext] Starting playback of new audio content"
          );
          newAudio.play().catch((err) => {
            console.error("[InterviewContext] Error playing audio:", err);
            // Still mark as not speaking and continue
            setIsInterviewerSpeaking(false);

            // Still start recording if needed even if playback fails
            if (shouldStartRecordingAfter && !isEnd && !isQuitting) {
              startRecording();
            }
          });
        } catch (e) {
          console.error(
            "[InterviewContext] Error in canplaythrough handler:",
            e
          );
          setIsInterviewerSpeaking(false);
        }
      };

      newAudio.onended = () => {
        console.log("[InterviewContext] Audio ended event fired");
        try {
          if (!isTemporary) {
            console.log("Audio playback ended, preparing for next steps");

            // Check both state and ref
            if (!isEnd && !isEndRef.current && !isQuitting) {
              resumeTranscription();

              if (shouldStartRecordingAfter) {
                // Check both state and ref one final time
                if (!isEnd && !isEndRef.current) {
                  console.log(
                    "Starting recording immediately after audio playback"
                  );
                  startRecording();
                } else {
                  console.log("Not starting recording - interview has ended");
                }
              }
            } else {
              console.log(
                "Not starting recording after audio - interview has ended or is quitting"
              );
            }

            // Use a shorter delay to set interviewer speaking state
            setTimeout(() => {
              console.log("Setting isInterviewerSpeaking to false");
              setIsInterviewerSpeaking(false);
            }, 100);
          } else {
            // For temporary audio, we don't start recording
            console.log("Temporary audio finished");
            setIsInterviewerSpeaking(false);
          }

          // Clean up the URL and remove the audio reference
          if (audioRef.current === newAudio) {
            audioRef.current = null;
          }
          URL.revokeObjectURL(audioUrl);
        } catch (e) {
          console.error("[InterviewContext] Error in ended handler:", e);
          setIsInterviewerSpeaking(false);
        }
      };

      newAudio.onerror = (e) => {
        console.error("[InterviewContext] Audio playback error:", e);
        setIsInterviewerSpeaking(false);

        // Still try to record if that was the intention
        if (shouldStartRecordingAfter && !isEnd && !isQuitting) {
          startRecording();
        }

        // Clean up the URL
        if (audioRef.current === newAudio) {
          audioRef.current = null;
        }
        URL.revokeObjectURL(audioUrl);
      };

      // Final check before setting source
      if (isQuitting) {
        console.log(
          "[InterviewContext] Quitting detected before setting audio src, aborting playback"
        );
        URL.revokeObjectURL(audioUrl);
        return;
      }

      // Set the source and store the element reference
      console.log(
        "[InterviewContext] Setting audio source and storing reference"
      );
      newAudio.src = audioUrl;
      audioRef.current = newAudio;

      // Load the audio to start the process
      console.log("[InterviewContext] Loading audio to begin playback process");
      newAudio.load();
    } catch (err) {
      console.error("[InterviewContext] Error in playAudioByContent:", err);
      // If something failed, ensure we reset the speaking state
      setIsInterviewerSpeaking(false);

      // If we were supposed to start recording, do it anyway
      if (shouldStartRecordingAfter && !isEnd && !isQuitting) {
        console.log("[InterviewContext] Starting recording after audio error");
        startRecording();
      }
    }
  };
  const stopAudio = () => {
    if (audioRef.current) {
      try {
        console.log("Stopping audio");
        const oldAudio = audioRef.current;

        // Explicitly remove event listeners to prevent any callback firing
        oldAudio.oncanplaythrough = null;
        oldAudio.onended = null;
        oldAudio.onerror = null;

        // Pause and reset playback position
        oldAudio.pause();
        oldAudio.currentTime = 0;

        // Clear the reference immediately to prevent replay
        audioRef.current = null;

        // Remove source attribute
        oldAudio.removeAttribute("src");

        // Release object URL if it exists
        if (oldAudio.src && oldAudio.src.startsWith("blob:")) {
          URL.revokeObjectURL(oldAudio.src);
        }

        // Ensure interviewer is marked as not speaking
        setIsInterviewerSpeaking(false);
      } catch (err) {
        console.error("Error stopping audio:", err);
        // Still reset speaking state even if error occurs
        setIsInterviewerSpeaking(false);
      }
    }
  };

  const resetInterview = () => {
    settIsEndHandler(false);
    isEndRef.current = false; // Reset the ref as well
    closeTranscription();
    cleanupAudioGraph(); // Make sure to clean up audio graph

    // Reset question numbers
    setCurrentQuestionNumber(0);
    setCaseQuestionNumber(0);

    // Reset recording-related state explicitly
    if (mediaRecorder && mediaRecorder.state === "recording") {
      try {
        mediaRecorder.stop();
      } catch (err) {
        console.warn("Error stopping MediaRecorder in resetInterview:", err);
      }
    }

    setMediaRecorder(null);
    setAudioChunks([]);
    setRecordingStarted(false);
    setShouldStartRecording(false);

    // Reset graphs and tips
    setGraphs([]);
    setTips("");
    setFallbackInformation([]);
    setFallbackInformationQuestionNumber(-1);

    // Reset the quitting flag after everything is reset
    setIsQuitting(false);
  };

  return (
    <InterviewContext.Provider
      value={{
        caseQuestionNumber,
        currentQuestionNumber,
        isInterviewerSpeaking,
        graphs,
        isEnd,
        sessionId,
        tips,
        resetInterview,
        startInterviewHandler,
        endInterviewHandler,
        quitInterviewHandler,
        finishAnsweringHandler,
        settIsEndHandler,
        setTipsHandler,
        isFirstTimeUser,
        questionTypes,
        playAudioByContent,
        stopAudio,
        checkInterviewCredits,
        audioRecordingUrl,
        fallbackInformation,
        fallbackInformationQuestionNumber,
      }}
    >
      {children}
    </InterviewContext.Provider>
  );
};
